import React from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, MessageCircle, Ticket, ArrowRight } from "lucide-react";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { SmokeReveal } from "../components/site/SmokeReveal";
import { Reveal } from "../components/site/Reveal";
import { WHATSAPP_NUMBER, ASSETS } from "@/lib/data";

const WA_LINK = `https://wa.me/${String(WHATSAPP_NUMBER).replace(/\D/g, "")}`;

const TICKETS = [
  {
    id: "normal", name: "Normal Ticket", tab: "NORMAL", price: "RM39", accent: "#00F0FF",
    tag: "General Admission", note: "3,000 pcs available",
    perks: [["Championship Ticket", true], ["Lucky Draw Coupon", false], ["Event Jersey", false], ["Event Goodies", false]],
  },
  {
    id: "combo", name: "Combo Ticket", tab: "COMBO", price: "RM59", accent: "#34D399",
    tag: "Best Value", note: "",
    perks: [["Championship Ticket", true], ["Lucky Draw Coupon", true], ["Event Jersey", true], ["Event Goodies", false]],
  },
  {
    id: "front-row-2", name: "Front Row #2", tab: "FRONT ROW", price: "RM99", accent: "#A78BFA",
    tag: "Ringside · Row 2", note: "Row 2 ringside seating", 
    perks: [["Championship Ticket", true], ["Lucky Draw Coupon", true], ["Event Jersey", true], ["Event Goodies", true]],
  },
  {
    id: "front-row-1", name: "Front Row #1", tab: "FRONT ROW", price: "RM149", accent: "#FF00D8",
    tag: "Closest To The Ring", note: "Row 1 — closest to the action",
    perks: [["Championship Ticket", true], ["Lucky Draw Coupon", true], ["Event Jersey", true], ["Event Goodies", true]],
  },
  {
    id: "vip", name: "VIP Table", tab: "VIP TABLE", price: "RM10,000", accent: "#FBBF24",
    tag: "Premium Experience", note: "Limited to 20 tables only",
    perks: [["Championship Ticket", true], ["Lucky Draw Coupon", true], ["Event Jersey", true], ["Event Goodies", true]],
    whatsapp: true,
  },
];

const TicketCard = ({ t }) => (
  <div
    className="group relative w-[272px] shrink-0 snap-center cursor-pointer overflow-visible rounded-[28px] border border-white/10 bg-gradient-to-b from-white/[0.09] to-white/[0.02] pb-4 backdrop-blur-xl transition-all duration-300 hover:-translate-y-3 hover:scale-[1.03]"
    style={{ boxShadow: `0 0 70px -20px ${t.accent}66, inset 0 1px 0 rgba(255,255,255,0.12)` }}
    data-testid={`ticket-card-${t.id}`}
  >
    {/* hover glow burst */}
    <div
      className="pointer-events-none absolute -inset-px rounded-[28px] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
      style={{ boxShadow: `0 24px 80px -12px ${t.accent}aa, 0 0 50px -8px ${t.accent}88`, border: `1px solid ${t.accent}66` }}
    />
    {/* glossy top highlight */}
    <div className="pointer-events-none absolute inset-x-8 top-0 h-px rounded-full bg-gradient-to-r from-transparent via-white/50 to-transparent transition-opacity duration-300 group-hover:via-white/80" />
    {/* vertical accent tab */}
    <div
      className="absolute -left-3 top-12 flex h-32 w-7 items-center justify-center rounded-full shadow-lg transition-transform duration-300 group-hover:-translate-y-1 group-hover:scale-110"
      style={{ background: `linear-gradient(180deg, ${t.accent}, ${t.accent}77)`, boxShadow: `0 0 24px ${t.accent}55` }}
    >
      <span className="rotate-180 font-mono-cap text-[9px] tracking-[0.25em] text-void" style={{ writingMode: "vertical-rl" }}>
        {t.tab}
      </span>
    </div>

    <div className="px-6 pt-6">
      <p className="text-center font-mono-cap text-[10px]" style={{ color: t.accent }}>{t.tag}</p>
      <p
        className="mt-3 text-center font-display text-[42px] leading-none transition-transform duration-300 group-hover:scale-110"
        style={{ color: t.accent, textShadow: `0 0 34px ${t.accent}66` }}
        data-testid={`ticket-price-${t.id}`}
      >
        {t.price}
      </p>
      <p className="mt-2 text-center font-sub text-sm font-bold uppercase tracking-wide text-white">{t.name}</p>

      <div className="my-5 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

      <ul className="space-y-2.5">
        {t.perks.map(([label, ok]) => (
          <li key={label} className="flex items-center gap-2.5 font-body text-[12px]">
            {ok
              ? <Check className="h-3.5 w-3.5 shrink-0" style={{ color: t.accent }} />
              : <X className="h-3.5 w-3.5 shrink-0 text-slate-700" />}
            <span className={ok ? "text-slate-200" : "text-slate-600"}>{label}</span>
          </li>
        ))}
      </ul>

      {t.note && <p className="mt-4 text-center font-mono-cap text-[9px] text-slate-500">{t.note}</p>}

      <div className="mt-5 flex justify-center">
        {t.whatsapp ? (
          <a
            href={WA_LINK}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 font-mono-cap text-[10px] transition-transform hover:scale-105"
            style={{ borderColor: `${t.accent}88`, color: t.accent }}
            data-testid="ticket-buy-vip"
          >
            <MessageCircle className="h-3.5 w-3.5" /> Enquire WhatsApp
          </a>
        ) : (
          <span
            className="inline-flex cursor-not-allowed items-center gap-2 rounded-full border px-5 py-2.5 font-mono-cap text-[10px]"
            style={{ borderColor: `${t.accent}55`, color: `${t.accent}99` }}
            data-testid={`ticket-buy-${t.id}`}
          >
            <Ticket className="h-3.5 w-3.5" /> Coming Soon
          </span>
        )}
      </div>
    </div>

    {/* bottom accent bar */}
    <div
      className="mt-5 flex h-9 items-center justify-center rounded-b-[28px]"
      style={{ background: `linear-gradient(90deg, ${t.accent}, ${t.accent}cc)` }}
    >
      <span className="font-mono-cap text-[10px] tracking-[0.2em] text-void">{t.name}</span>
    </div>
  </div>
);

export default function TicketsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-void text-white" data-testid="tickets-page">
      <Header />
      <main className="mx-auto max-w-7xl px-4 pb-20 pt-28 sm:px-8">
        <SmokeReveal className="mx-auto max-w-3xl text-center">
          <p className="font-mono-cap text-[11px] text-neon-cyan">WPC 3.0 · 25–28 November 2026 · Stadium Titiwangsa, KL</p>
          <h1 className="mt-4 font-display text-4xl leading-[0.95] sm:text-5xl">
            TICKET <span className="gradient-text">CATEGORIES</span>
          </h1>
          <p className="mt-4 font-body text-sm text-slate-400 sm:text-base">
            Choose your championship ticket category below. Ticket sales will be announced soon.
          </p>
        </SmokeReveal>

        <Reveal delay={0.05} className="mx-auto mt-10 max-w-md">
          <img src={ASSETS.aboutPoster3} alt="WPC 3.0 — The Rise of Pahlawan official event poster" className="w-full rounded-3xl border border-white/10 glow-ring" data-testid="tickets-poster" />
        </Reveal>

        <Reveal delay={0.1} className="relative mt-4">
          <div className="flex flex-col items-center gap-7 pb-4 pt-10" data-testid="tickets-track">
            <div className="flex flex-wrap items-stretch justify-center gap-7" data-testid="tickets-row-1">
              {TICKETS.slice(0, 2).map((t) => <TicketCard key={t.id} t={t} />)}
            </div>
            <div className="flex flex-wrap items-stretch justify-center gap-7" data-testid="tickets-row-2">
              {TICKETS.slice(2).map((t) => <TicketCard key={t.id} t={t} />)}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="mx-auto mt-14 max-w-3xl">
          <div className="rounded-2xl border border-neon-violet/30 bg-surface/80 p-6 text-center" data-testid="tickets-register-note">
            <p className="font-display text-2xl text-white">Want to compete or join the seminar?</p>
            <p className="mt-2 font-body text-xs text-slate-400">
              Amateur (RM89 · 400 slots), Adult Seminar (RM169) and Kids Seminar (RM119) registrations are open now.
            </p>
            <button
              onClick={() => navigate("/register")}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-neon-pink to-neon-violet px-6 py-3 font-mono-cap text-[11px] text-white transition-transform hover:scale-105"
              data-testid="tickets-go-register"
            >
              Go To Registration Hub <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </Reveal>
      </main>
      <Footer />
    </div>
  );
}
